import { useEffect, useMemo, useRef } from 'react';
import { throttle } from 'lodash';
import type { ArdoqId } from '@ardoq/api-types';
import type { ComponentMatrix2Commands } from './types';

const MOUSE_MOVE_EVENT_THROTTLE = 50;

type HoveredItemHandlers = {
  onMouseEnter: VoidFunction;
  onMouseLeave: VoidFunction;
};

export const useHoveredItemHandlers = (
  componentId: ArdoqId,
  {
    onItemMouseEnter,
    onItemMouseLeave,
  }: Pick<ComponentMatrix2Commands, 'onItemMouseEnter' | 'onItemMouseLeave'>
): HoveredItemHandlers => {
  const isPointerInsideRef = useRef(false);
  const throttledOnItemMouseEnter = useMemo(
    () =>
      throttle(() => {
        if (!isPointerInsideRef.current) {
          return;
        }
        onItemMouseEnter(componentId);
      }, MOUSE_MOVE_EVENT_THROTTLE),
    [componentId, onItemMouseEnter]
  );
  useEffect(
    () => () => throttledOnItemMouseEnter.cancel(),
    [throttledOnItemMouseEnter]
  );

  return {
    onMouseEnter: () => {
      isPointerInsideRef.current = true;
      throttledOnItemMouseEnter();
    },
    onMouseLeave: () => {
      isPointerInsideRef.current = false;
      onItemMouseLeave();
    },
  };
};
